import { Badge, Group } from '@repletoryreach/sdk-ui';
import { StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { templates } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';

type Status = NonNullable<Awaited<ReturnType<typeof templates.list>>['data']>[number]['status'];

/**
 * Lifecycle state as a design-system tone rather than a hand-picked colour, so
 * the chip repaints with the theme like every other status in the console.
 */
const TONES: Record<Status, StatusTone> = {
  draft: 'inactive',
  submitted: 'pending',
  approved: 'healthy',
  rejected: 'down',
  paused: 'warning'
};

/** Catalog keys for the Meta lifecycle status labels (shared `status.*` vocabulary). */
export const STATUS_LABEL_KEYS: Record<Status, string> = {
  draft: 'status.draft',
  submitted: 'status.submitted',
  approved: 'status.approved',
  rejected: 'status.rejected',
  paused: 'status.paused'
};

export interface TemplateStatusBadgeProps {
  status: Status;
  /**
   * True when a non-production fallback approved the template instead of Meta.
   * It sets `approved_at` exactly as a real approval does, so without this chip
   * a green "Approved" in a staging console is indistinguishable from one Meta
   * actually granted, and the SDK is explicit that such a template must be
   * treated as unapproved for a real send.
   */
  devFallback?: boolean;
  /**
   * Meta's own review word, as last read from the Graph API. Shown muted beside
   * ours: where the two disagree, that divergence is the signal this page
   * exists to give.
   */
  metaStatus?: string | null;
}

export function TemplateStatusBadge({ status, devFallback, metaStatus }: TemplateStatusBadgeProps) {
  const t = useT();
  const divergent = Boolean(metaStatus) && metaStatus!.toLowerCase() !== status.toLowerCase();

  return (
    <Group gap="xs" wrap="nowrap">
      <StatusBadge status={TONES[status]} size="xs">{t(STATUS_LABEL_KEYS[status])}</StatusBadge>
      {devFallback && (
        <Tooltip label={t('templates.devApprovedTooltip')} position="top" openDelay={200}>
          <Badge size="xs" variant="outline" color="orange">{t('templates.devApproved')}</Badge>
        </Tooltip>
      )}
      {divergent && (
        <Tooltip label={t('templates.metaStatusTooltip')} position="top" openDelay={200}>
          <Badge size="xs" variant="light" color="gray">{t('templates.metaStatus', { status: metaStatus! })}</Badge>
        </Tooltip>
      )}
    </Group>
  );
}
